import React, { useEffect, useLayoutEffect, useState } from 'react'
import {
   Box,
   DialogActions,
   DialogContent,
   FormControl,
   FormControlLabel,
   IconButton,
   Radio,
   RadioGroup,
   Table,
   TableBody,
   TableCell,
   TableHead,
   TableRow,
   TextField,
   Typography,
} from '@mui/material'

import { MarkupSettingsType } from 'shared/store/useLocations/types'
import OutlinedButton from 'shared/components/Buttons/OutlinedButton'
import ContainedButton from 'shared/components/Buttons/ContainedButton'
import { useTranslation } from 'react-i18next'
import { Close, Search, Add } from '@mui/icons-material'
import { isAxiosError } from 'axios'
import { useFormik, FormikProvider } from 'formik'
import InputField from 'shared/components/InputField'
import MuiPhone from 'shared/components/PhoneInput'
import { useNotificationContext } from 'shared/context/Notifications'
import getCompanyData from 'shared/services/getCompanyData'
import { CustomerType } from 'shared/store/useCustomers/types'
import validatePhoneNumber from 'shared/utils/helpers/validatePhoneNumber'
import { InfinityThresholdType } from 'views/Authorized/views/Locations/types'
import { useLocationsQuery } from 'shared/store/useLocations'
import { useProfileQuery } from 'shared/store/useProfile'
import { useAddCustomerMutation } from 'shared/store/useCustomers'
import { useEditReflinkMutation } from 'shared/store/useClientReflinks'
import { EditReflinkPayload, ReflinkType } from 'shared/store/useClientReflinks/types'

interface Props {
   phoneNumber?: string
   email?: string
   reflinkSettings?: ReflinkType
   markup: Nullable<MarkupSettingsType>
   cartName: string
   handleClose: () => void
}

export default function AddCustomerModal({
   phoneNumber,
   reflinkSettings,
   handleClose,
   email,
   markup,
   cartName,
}: Props) {
   const { t } = useTranslation(['TRANSLATION', 'CUSTOMERS'])
   const { addNotification } = useNotificationContext()
   const [infinityThreshold, setInfinityThreshold] = useState<InfinityThresholdType>({
      multiplier: 1,
      addend: 0,
   })
   const [markupSettings, setMarkupSettings] = useState<Nullable<MarkupSettingsType>>(markup)
   const { data: profileSettings } = useProfileQuery()
   const { data: locations } = useLocationsQuery()
   const { mutate: addCustomer } = useAddCustomerMutation()
   const { mutate: updateReflink } = useEditReflinkMutation()
   const [isCompanyDataFetching, setIsCompanyDataFetching] = useState(false)
   const [isCompany, setIsCompany] = useState(false)
   const [verificationMethod, setVerificationMethod] = useState<'SMS' | 'EMAIL'>('SMS')
   const [phoneNumberError, setPhoneNumberError] = useState<boolean>(false)

   const formik = useFormik<CustomerType>({
      initialValues: {
         name: '',
         surname: '',
         uuid: '',
         displayName: cartName || '',
         companyName: cartName || '',
         contacts: [
            {
               name: '',
               surname: '',
               email: email ?? '',
               phoneNumber: phoneNumber ?? '',
               comment: '',
               uuid: '',
            },
         ],
         taxID: '',
         deliveryAddresses: [],
         deliveryType: '',
         paymentMethod: '',
         address: {
            street: '',
            houseNumber: '',
            zipCode: '',
            city: '',
            country: 'Polska',
            voivodeship: '',
            county: '',
         },
         productsMarkupSettings: {
            priceType: markup?.priceType || 'GROSS',
            thresholds: markup?.thresholds || [],
         },
      },
      onSubmit: () => {
         if (values.contacts[0].phoneNumber.length < 4 && !values.contacts[0].email) {
            addNotification(t('CLIENTS_CARTS:contactDetailsError'), 'error')
         } else if ((!!values.name && !!values.surname) || (!!values.taxID && !!values.companyName)) {
            addCustomer(
               {
                  ...values,
                  productsMarkupSettings: {
                     priceType: markupSettings?.priceType || 'GROSS',
                     thresholds: markupSettings?.thresholds || [],
                  },
                  name: values.name ? values.name : null,
                  surname: values.surname ? values.surname : null,
                  companyName: values.companyName ? values.companyName : null,
                  taxID: values.taxID ? values.taxID : null,
                  paymentMethod: values.paymentMethod ? values.paymentMethod : null,
                  deliveryType: values.deliveryType ? values.deliveryType : null,
               },
               {
                  onSuccess: (data) => {
                     const { carts, ...rest } = reflinkSettings!
                     if (data.status === 200) {
                        const reflinkPayload: EditReflinkPayload = {
                           ...rest,
                           verificationMethod,
                           clientUuid: data.data.uuid,
                           clientContactUuid: data.data.contacts[0].uuid,
                        }
                        updateReflink(
                           { payload: reflinkPayload, reflinkUuid: reflinkSettings!.uuid },
                           {
                              onSuccess: () => {
                                 handleClose()
                              },
                           }
                        )
                     }
                  },
               }
            )
         } else {
            addNotification(t('CLIENTS_CARTS:noCompanyOrCustomerDetailsError'), 'error')
         }
      },
   })

   const { values, setFieldValue, setValues, validateForm, setTouched, setFieldError, handleSubmit, setErrors } = formik

   function touchAllFields(obj: any): any {
      if (obj !== null && typeof obj === 'object' && !Array.isArray(obj)) {
         return Object.keys(obj).reduce(
            (acc, key) => {
               acc[key] = touchAllFields(obj[key])
               return acc
            },
            {} as { [key: string]: boolean | typeof obj }
         )
      }

      return true
   }

   function handlePhoneNumber(value: string, country: string, index: number) {
      setFieldValue(`contacts[${index}].phoneNumber`, value)
      const result = validatePhoneNumber(value, country)
      if (result === false) {
         setPhoneNumberError(true)
      } else {
         setPhoneNumberError(false)
      }
   }

   async function handleOnSearch() {
      setIsCompanyDataFetching(true)
      await getCompanyData(values.taxID!)
         .then((res) => {
            if (res) {
               setValues(
                  {
                     ...values,
                     companyName: res.companyName,
                     displayName: res.companyName,
                     address: {
                        country: 'Polska',
                        voivodeship: res.voivodeship ?? values.address.voivodeship,
                        houseNumber: res.houseNumber ?? values.address.houseNumber,
                        county: res.county ?? values.address.county,
                        city: res.city ?? values.address.city,
                        street: res.street ?? values.address.street,
                        zipCode: res.postalCode ?? values.address.zipCode,
                     },
                  },
                  true
               )
            }
            validateForm({
               ...values,
               displayName: res.companyName,
               companyName: res.companyName,
               address: {
                  country: 'Polska',
                  voivodeship: res.voivodeship,
                  houseNumber: res.houseNumber,
                  county: res.county,
                  city: res.city,
                  street: res.street,
                  zipCode: res.postalCode,
               },
            }).then((errors) => {
               const allFieldsTouched = touchAllFields(values)
               setTouched(allFieldsTouched).then(() => {
                  setErrors(errors)
               })
            })
         })
         .catch((error) => {
            if (isAxiosError<ErrorType>(error)) {
               setFieldError('taxID', t(`TRANSLATION:${error.response?.data?.errors[0].code}`))
            } else {
               setFieldError('taxID', error)
            }
         })
         .finally(() => setIsCompanyDataFetching(false))
   }

   useEffect(() => {
      if (!formik.touched.displayName && (!!values.name || !!values.surname)) {
         setFieldValue('displayName', `${values.name} ${values.surname}`)
      }
      if (values.contacts[0]) {
         if (!isCompany) {
            setFieldValue('contacts[0].name', values.name)
            setFieldValue('contacts[0].surname', values.surname)
         }
      }
   }, [values.name, values.surname])

   useEffect(() => {
      if (markup?.thresholds) {
         setInfinityThreshold({
            multiplier: markup.thresholds[markup.thresholds.length - 1].multiplier,
            addend: markup.thresholds[markup.thresholds.length - 1].addend,
         })
      } else {
         const mainLocationMarkup = locations?.locations.find(
            (location) => location.locationSettings.uuid === profileSettings?.locationUuid
         )?.markupSettings
         if (mainLocationMarkup?.thresholds) {
            setInfinityThreshold({
               multiplier: mainLocationMarkup.thresholds[mainLocationMarkup.thresholds.length - 1].multiplier,
               addend: mainLocationMarkup.thresholds[mainLocationMarkup.thresholds.length - 1].addend,
            })
            setMarkupSettings(mainLocationMarkup)
         }
      }
   }, [markup])

   useEffect(() => {
      setTouched({ displayName: true })
   }, [])

   useEffect(() => {
      if (!email) {
         setVerificationMethod('SMS')
      } else {
         setVerificationMethod('EMAIL')
      }
   }, [email, phoneNumber])

   return (
      <Box>
         <DialogContent>
            <FormikProvider value={formik}>
               <Box
                  sx={{
                     display: 'flex',
                     py: '8px',
                     flexDirection: 'column',
                     minWidth: { lg: '750px', md: '500px', xs: 'auto' },
                     gap: '8px',
                  }}
               >
                  <Box sx={{ display: 'flex', gap: '8px', justifyContent: 'center', alignItems: 'center' }}>
                     <FormControlLabel
                        onClick={() => setIsCompany(true)}
                        checked={isCompany}
                        control={<Radio />}
                        label={t('CLIENTS_CARTS:markup.new.company')}
                     />
                     <FormControlLabel
                        onClick={() => setIsCompany(false)}
                        checked={!isCompany}
                        control={<Radio />}
                        label={t('CLIENTS_CARTS:markup.new.person')}
                     />
                  </Box>
                  {isCompany ? (
                     <>
                        <Typography variant="h6">{t('CLIENTS_CARTS:markup.new.companyData')}</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                           <InputField
                              sx={{ flex: 0.33 }}
                              slotProps={{
                                 input: {
                                    endAdornment: (
                                       <ContainedButton
                                          disabled={isCompanyDataFetching || !values.taxID}
                                          onClick={handleOnSearch}
                                          variant="contained"
                                       >
                                          <Search />
                                       </ContainedButton>
                                    ),
                                 },
                              }}
                              name="taxID"
                              fullWidth
                              label={t('TRANSLATION:formFields.taxID')}
                           />

                           <InputField
                              sx={{ flex: 0.67 }}
                              name="companyName"
                              fullWidth
                              label={t('TRANSLATION:formFields.companyName')}
                           />
                        </Box>
                        <InputField fullWidth name="displayName" label="Nazwa" />
                     </>
                  ) : (
                     <>
                        <Typography variant="h6">{t('CLIENTS_CARTS:markup.new.clientData')}</Typography>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                           <InputField name="name" fullWidth label={t('TRANSLATION:formFields.name')} />

                           <InputField name="surname" fullWidth label={t('TRANSLATION:formFields.surname')} />
                        </Box>
                        <InputField fullWidth name="displayName" label="Nazwa" />
                     </>
                  )}
                  <FormControl>
                     <RadioGroup
                        value={verificationMethod}
                        onChange={(e) => {
                           setVerificationMethod(e.target.value as 'SMS' | 'EMAIL')
                        }}
                        row
                     >
                        <FormControlLabel
                           value="SMS"
                           disabled={!values.contacts[0].phoneNumber}
                           control={<Radio />}
                           label={t('TRANSLATION:columnNames.phoneNumber')}
                        />
                        <FormControlLabel
                           disabled={!values.contacts[0].email}
                           value="EMAIL"
                           control={<Radio />}
                           label="Email"
                        />
                     </RadioGroup>
                  </FormControl>
                  <Typography variant="h6">{t('CLIENTS_CARTS:markup.new.customerData')}</Typography>
                  {values.contacts.map((contact, index) => (
                     <Box sx={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                           <Box sx={{ display: 'flex', gap: '8px' }}>
                              <InputField
                                 name={`contacts[${index}]name`}
                                 fullWidth
                                 label={t('TRANSLATION:formFields.name')}
                              />
                              <InputField
                                 name={`contacts[${index}]surname`}
                                 fullWidth
                                 label={t('TRANSLATION:formFields.surname')}
                              />
                              <InputField name={`contacts[${index}]email`} fullWidth label="E-mail" />
                              <MuiPhone
                                 notRequired
                                 label={t('TRANSLATION:columnNames.phoneNumber')}
                                 variant="outlined"
                                 value={values.contacts[index].phoneNumber!}
                                 onChange={(value, country) => handlePhoneNumber(value, country, index)}
                              />
                           </Box>
                           <InputField
                              name={`contacts[${index}].comment`}
                              fullWidth
                              multiline
                              rows={2}
                              label={t('TRANSLATION:columnNames.description')}
                           />
                        </Box>
                        <IconButton
                           onClick={() => {
                              const updated = [...values.contacts]
                              updated.splice(index, 1)
                              setFieldValue('contacts', updated)
                           }}
                           sx={{ '&:hover': { color: 'red' } }}
                        >
                           <Close />
                        </IconButton>
                     </Box>
                  ))}

                  <IconButton
                     sx={{ alignSelf: 'center' }}
                     onClick={() => {
                        const newContacts = [...values.contacts]
                        newContacts.push({
                           name: '',
                           uuid: '',
                           surname: '',
                           email: '',
                           phoneNumber: '',
                           comment: '',
                        })
                        setFieldValue('contacts', newContacts)
                     }}
                  >
                     <Add />
                  </IconButton>
               </Box>
               <Typography variant="h6">{t('CLIENTS_CARTS:markup.new.markupSettings')}</Typography>
               <Box sx={{ maxHeight: 450, width: '100%', overflowY: 'scroll' }}>
                  <Table stickyHeader>
                     <TableHead>
                        <TableRow>
                           <TableCell sx={{ textAlign: 'center' }}>
                              {t('CLIENTS_CARTS:markupSettings.start', {
                                 priceType: t(`TRANSLATION:${markupSettings?.priceType}`),
                              })}
                           </TableCell>
                           <TableCell sx={{ textAlign: 'center' }}>
                              {t('CLIENTS_CARTS:markupSettings.end', {
                                 priceType: t(`TRANSLATION:${markupSettings?.priceType}`),
                              })}
                           </TableCell>
                           <TableCell sx={{ textAlign: 'center' }}>
                              {t('CLIENTS_CARTS:markupSettings.multiplier')}
                           </TableCell>
                           <TableCell sx={{ textAlign: 'center' }}>
                              {' '}
                              {t('CLIENTS_CARTS:markupSettings.addend')}
                           </TableCell>
                           <TableCell sx={{ textAlign: 'center' }} />
                        </TableRow>
                     </TableHead>
                     <TableBody>
                        {markupSettings?.thresholds &&
                           markupSettings.thresholds.length > 0 &&
                           markupSettings.thresholds.map((item, markupIndex) => {
                              const isLastItem = markupIndex === markupSettings.thresholds.length - 1
                              return (
                                 // eslint-disable-next-line react/no-array-index-key
                                 <TableRow sx={{}} key={markupIndex}>
                                    <TableCell
                                       sx={{
                                          textAlign: 'center',
                                          padding: { lg: '16px', md: '12px', xs: '8px' },
                                       }}
                                    >
                                       <TextField
                                          sx={{
                                             '& .MuiInputBase-input': {
                                                py: 1,
                                                width: { lg: 100, md: 50, xs: 50 },
                                             },
                                          }}
                                          type="number"
                                          variant="outlined"
                                          value={item.start}
                                          slotProps={{
                                             htmlInput: {
                                                readOnly: true,
                                                min: item.start,
                                                step: 0.01,
                                             },
                                          }}
                                       />
                                    </TableCell>
                                    <TableCell
                                       sx={{
                                          textAlign: 'center',
                                          padding: { lg: '16px', md: '12px', xs: '8px' },
                                       }}
                                    >
                                       <TextField
                                          sx={{
                                             '& .MuiInputBase-input': {
                                                ...(item.end && {
                                                   py: 1,
                                                   width: { lg: 100, md: 50, xs: 50 },
                                                }),
                                                ...(!item.end && {
                                                   textAlign: 'center',
                                                   fontSize: '24px',
                                                   paddingY: 0,
                                                   width: { lg: 100, md: 50, xs: 50 },
                                                }),
                                             },
                                          }}
                                          type={item.end ? 'Number' : 'string'}
                                          variant="outlined"
                                          disabled={isLastItem}
                                          value={!item.end ? '∞' : item.end}
                                          slotProps={{
                                             htmlInput: {
                                                readOnly: true,
                                                min: item.start + 0.01,
                                                step: 0.01,
                                             },
                                          }}
                                       />
                                    </TableCell>
                                    <TableCell
                                       sx={{
                                          textAlign: 'center',
                                          padding: { lg: '16px', md: '12px', xs: '8px' },
                                       }}
                                    >
                                       <TextField
                                          sx={{
                                             '& .MuiInputBase-input': {
                                                py: 1,
                                                width: { lg: 100, md: 50, xs: 50 },
                                             },
                                          }}
                                          type="number"
                                          onFocus={(e) => e.target.select()}
                                          variant="outlined"
                                          value={item.multiplier}
                                          slotProps={{
                                             htmlInput: {
                                                readOnly: true,
                                                min: 1,
                                                step: 0.01,
                                             },
                                          }}
                                       />
                                    </TableCell>
                                    <TableCell
                                       sx={{
                                          textAlign: 'center',
                                          padding: { lg: '16px', md: '12px', xs: '8px' },
                                       }}
                                    >
                                       <TextField
                                          sx={{
                                             '& .MuiInputBase-input': {
                                                py: 1,
                                                width: { lg: 100, md: 50, xs: 50 },
                                             },
                                          }}
                                          type="Number"
                                          onFocus={(e) => e.target.select()}
                                          variant="outlined"
                                          value={item.addend}
                                          slotProps={{
                                             htmlInput: {
                                                readOnly: true,
                                                min: 1,
                                                step: 1,
                                             },
                                          }}
                                       />
                                    </TableCell>
                                 </TableRow>
                              )
                           })}
                     </TableBody>
                  </Table>
               </Box>
            </FormikProvider>
         </DialogContent>
         <DialogActions>
            <OutlinedButton variant="outlined" onClick={handleClose}>
               {t('TRANSLATION:cancel')}
            </OutlinedButton>
            <ContainedButton variant="contained" onClick={() => handleSubmit()}>
               {t('TRANSLATION:save')}
            </ContainedButton>
         </DialogActions>
      </Box>
   )
}
